// In-app filename prompt: a native <dialog> so Save / Save As / rename
// can ask for a name uniformly on every browser (Chromium's picker
// exposes a name field, Firefox's download does not — this normalises
// the UX). Built lazily on first open, mirroring login-modal.ts.
//
// Returns the chosen filename (normalised to end in `.sas`) or null if
// the user cancelled (Esc / click-outside / X / empty submit).

import { ensureSasName } from './save';

const DIALOG_ID = 'name-modal';

interface ModalRefs {
  dialog: HTMLDialogElement;
  title: HTMLElement;
  input: HTMLInputElement;
  error: HTMLElement;
  okBtn: HTMLButtonElement;
}

interface PromptOptions {
  title: string;
  initial?: string;
  okLabel?: string;
}

let refs: ModalRefs | null = null;
// Resolver for the in-flight prompt; settled exactly once per open via
// `settle()`.
let pending: ((value: string | null) => void) | null = null;

function settle(value: string | null): void {
  const resolve = pending;
  pending = null;
  if (resolve) resolve(value);
}

function build(): ModalRefs {
  let dialog = document.getElementById(DIALOG_ID) as HTMLDialogElement | null;
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.id = DIALOG_ID;
    document.body.appendChild(dialog);
  }
  dialog.classList.add('name-modal');
  dialog.innerHTML = '';

  const title = document.createElement('div');
  title.className = 'name-title';
  dialog.appendChild(title);

  const form = document.createElement('form');
  form.className = 'name-form';
  form.noValidate = true;

  const input = document.createElement('input');
  input.className = 'name-input';
  input.type = 'text';
  input.autocomplete = 'off';
  input.spellcheck = false;
  form.appendChild(input);

  const error = document.createElement('div');
  error.className = 'name-error';
  error.hidden = true;
  form.appendChild(error);

  const actions = document.createElement('div');
  actions.className = 'name-actions';
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => dialog!.close());
  actions.appendChild(cancelBtn);
  const okBtn = document.createElement('button');
  okBtn.type = 'submit';
  okBtn.className = 'btn btn-primary';
  okBtn.textContent = 'Save';
  actions.appendChild(okBtn);
  form.appendChild(actions);

  dialog.appendChild(form);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'name-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => dialog!.close());
  dialog.appendChild(closeBtn);

  // Click on the backdrop (target === dialog) cancels.
  dialog.addEventListener('click', (ev) => {
    if (ev.target === dialog) dialog!.close();
  });

  // Esc, backdrop, X, and Cancel all funnel through the native `close`
  // event → resolve null. A successful submit closes after settling
  // with the value, so `pending` is already cleared by then.
  dialog.addEventListener('close', () => settle(null));

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const result = validate(input.value);
    if (result.error) {
      error.textContent = result.error;
      error.hidden = false;
      return;
    }
    settle(result.name);
    dialog!.close();
  });

  return { dialog, title, input, error, okBtn };
}

interface Validated {
  name: string;
  error: string | null;
}

// C0 control chars + DEL — stripped so a pasted name can't smuggle in
// newlines or NULs.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp('[\u0000-\u001f\u007f]', 'g');

function validate(raw: string): Validated {
  const cleaned = raw.replace(CONTROL_CHARS, '').trim();
  if (cleaned.length === 0) return { name: '', error: 'Enter a file name.' };
  if (/[/\\]/.test(cleaned)) return { name: '', error: 'File name can’t contain / or \\.' };
  return { name: ensureSasName(cleaned), error: null };
}

/// Open the prompt and resolve with the chosen `.sas` filename, or null
/// if cancelled. A second call while one is open settles the first as
/// cancelled (shouldn't happen in practice — prompts are modal).
export function promptFilename(opts: PromptOptions): Promise<string | null> {
  if (!refs) refs = build();
  settle(null);

  const r = refs;
  r.title.textContent = opts.title;
  r.okBtn.textContent = opts.okLabel ?? 'Save';
  r.error.hidden = true;
  r.error.textContent = '';
  r.input.value = opts.initial ?? '';

  return new Promise<string | null>((resolve) => {
    pending = resolve;
    if (typeof r.dialog.showModal === 'function') r.dialog.showModal();
    else r.dialog.setAttribute('open', '');
    r.input.focus();
    // Pre-select the basename (everything before the extension) so the
    // user can retype the name without nuking the `.sas` suffix.
    const dot = r.input.value.lastIndexOf('.');
    if (dot > 0) r.input.setSelectionRange(0, dot);
    else r.input.select();
  });
}
